import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import clsx from 'clsx';
import { Context } from '../stateManagement/store';
import useActionsHook from '../stateManagement/actions';
import { getSupportedWallets } from '../services/providerService';
import ThreeDotsLoader from './Icons/ThreeDotsLoader';
import PendingStatusIcon from './Icons/PendingStatusIcon';
import { networks } from '../constants/networks';
import { formatNumber } from '../services/utils';

interface IButton {
  pendingTx: number
  showBalance?: boolean
  onClick: (e?: any) => void
}

let interval: number;

const Button: React.FC<IButton> = ({
  pendingTx = 0,
  showBalance,
  onClick = () => {},
}) => {
  const { dispatch, state } = useContext(Context);
  const {
    account, connectingWalletType, accountType, networkId, balanceWei, balanceLoading,
  } = state;

  const actions = useActionsHook(dispatch);

  const onOpenModalClick = useCallback((e) => {
    actions.openModal();
    if (onClick) {
      onClick(e);
    }
  }, [actions, onClick]);

  const balance = useMemo(() => {
    if (!networkId) {
      return null
    }
    const { decimals, symbol } = networks[networkId]?.nativeCurrency;
    if (!parseInt(balanceWei, 16)) {
      return {
        symbol,
        amount: '0.0',
      };
    }
    return {
      amount: formatNumber((parseInt(balanceWei, 16) / (10 ** decimals)).toString(), 2, true, true),
      symbol,
    };
  }, [balanceWei, networkId]);

  const walletInfo = getSupportedWallets().find(({ connectionType }) => connectionType === accountType);
  const shortAddress = useMemo(() => `${account?.slice(0, 5)}...${account?.slice(-5)}`, [account]);

  useEffect(() => {
    if (account && showBalance) {
      actions.getBaseTokenBalance(account);
      if (interval) {
        clearInterval(interval);
      }
      interval = window.setInterval(() => {
        actions.getBaseTokenBalance(account);
      }, 5 * 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [actions, account, showBalance]);

  return (
    <button type="button" onClick={onOpenModalClick} className={clsx('web3-wc_connect-wallet-button', { connected: account, withBalance: showBalance })}>
      {account ? (
        <div className="web3-wc_connect-wallet-button_connected">
          {showBalance && (
            <div className="web3-wc_connect-wallet-button_balance">
              {balance?.amount
                ? <>{balance.amount} {balance.symbol}</>
                : (balanceLoading && <ThreeDotsLoader />)}
            </div>
          )}
          {pendingTx ? (
            <div className="web3-wc_animate-round">
              {pendingTx} Pending
                    &nbsp;
              <PendingStatusIcon />
            </div>
          ) : (
            <div className="web3-wc_short-address">{shortAddress}</div>
          )}
          <div className="web3-wc_icon-status-wrapper">
            <div className="web3-wc_icon-wrapper"><walletInfo.Icon /></div>
            {/*<div className={clsx('web3-wc_status', account ? 'connected' : '')} />*/}
          </div>
        </div>
      ) : (connectingWalletType && <ThreeDotsLoader />) || 'Connect Wallet'}
    </button>
  );
};

export default Button;
